import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import client from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import AccessDenied from "@/components/AccessDenied";
import { KpiCard } from "@/components/KpiCard";
import AIInsightsPanel from "@/components/AIInsightsPanel";
import { RevenueVsBudgetChart, EbitdaMarginChart, CashFlowChart, SectorBenchmarkChart } from "@/components/Charts";
import SectorKPIs from "@/components/SectorKPIs";
import NewsWidget from "@/components/NewsWidget";
import { fmtMoney, fmtPctPlain, fmtNum } from "@/lib/format";
import { ArrowRight, ArrowLeft, Warning } from "@phosphor-icons/react";

export default function SectorView() {
    const { sectorId } = useParams();
    const [data, setData] = useState(null);
    const [intel, setIntel] = useState(null);
    const [denied, setDenied] = useState(false);
    const isWatchlist = typeof window !== "undefined" && window.location.search.includes("watchlist=1");
    useEffect(() => {
        setData(null); setDenied(false); setIntel(null);
        client.get(`/dashboard/sector/${sectorId}`).then((r) => setData(r.data)).catch((e) => { if (e.response?.status === 403) setDenied(true); });
        client.get(`/intel/sector/${sectorId}`).then((r) => setIntel(r.data)).catch(() => {});
    }, [sectorId]);

    if (denied) return <AccessDenied />;
    if (!data) return <div className="p-16 font-serif text-3xl text-fp-ink">Loading…</div>;
    const s = data.snapshot;
    const c = data.consolidated;

    return (
        <div>
            <PageHeader
                overline={<Link to="/" className="hover:text-fp-ink inline-flex items-center gap-1"><ArrowLeft size={11} /> Group Overview</Link>}
                title={data.scope_label}
                description={`${c.entity_count} operating entities · ${data.entity_breakdown.length} country presence`}
                period={s.period_label}
            />

            <div className="px-4 sm:px-6 lg:px-12 py-10 space-y-10">
                {isWatchlist && (
                    <div className="bg-fp-terracotta/10 border-l-4 border-fp-terracotta p-5 flex items-start gap-3" data-testid="watchlist-banner">
                        <Warning size={18} weight="fill" className="text-fp-terracotta shrink-0 mt-0.5" />
                        <div>
                            <div className="font-serif text-lg text-fp-ink">Performance Watchlist</div>
                            <div className="font-sans text-sm text-fp-secondary mt-1">This division is flagged for management attention based on YoY performance and margin trajectory. Key drivers and management attention items are highlighted below — review, investigate, monitor and consider corrective actions.</div>
                        </div>
                    </div>
                )}
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 lg:gap-5">
                    <KpiCard label="Revenue" value={fmtMoney(s.revenue.value_musd, { decimals: 0 })} subValue={`vs Budget: ${fmtPctPlain(s.revenue.budget_variance_pct)}`} delta={s.revenue.yoy_pct} deltaLabel="YoY" trend={s.revenue.trend} testId="sector-kpi-revenue" variant="primary" />
                    <KpiCard label="EBITDA" value={fmtMoney(s.ebitda.value_musd, { decimals: 0 })} subValue={`Margin: ${fmtPctPlain(s.ebitda.margin_pct)}`} delta={s.ebitda.mom_pct} deltaLabel="MoM" trend={s.ebitda.trend} testId="sector-kpi-ebitda" variant="success" />
                    <KpiCard label="Free Cash Flow" value={fmtMoney(s.free_cash_flow.value_musd, { decimals: 0 })} trend={s.free_cash_flow.trend} testId="sector-kpi-fcf" variant="teal" />
                    <KpiCard label="ROIC" value={fmtPctPlain(s.roic_pct)} subValue={`ROE: ${fmtPctPlain(s.roe_pct)}`} testId="sector-kpi-roic" variant="plum" />
                    <KpiCard label="Headcount" value={fmtNum(s.headcount)} subValue={`Attrition: ${fmtPctPlain(s.attrition_pct)}`} testId="sector-kpi-hc" variant="warn" />
                </div>

                {/* Sector-specific KPIs (benchmarked) */}
                {intel?.specific_kpis?.length > 0 && <SectorKPIs kpis={intel.specific_kpis} />}

                {/* AI + Benchmark chart + News */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                    <div className="lg:col-span-4">
                        <AIInsightsPanel context={`sector:${sectorId}`} contextLabel={data.scope_label} />
                    </div>
                    <div className="lg:col-span-5 bg-white border border-fp-border p-5 sm:p-6 lg:p-8">
                        <div className="mb-6">
                            <div className="font-sans text-[11px] uppercase tracking-[0.2em] text-fp-muted mb-1">Peer Positioning</div>
                            <h3 className="font-serif text-2xl text-fp-ink">Benchmark · Revenue & Margin</h3>
                        </div>
                        <SectorBenchmarkChart sectorName={data.scope_label.split(" ")[0]} revenue={c.pnl.revenue} ebitdaMargin={c.pnl.ebitda_margin_pct} />
                    </div>
                    <div className="lg:col-span-3">
                        <NewsWidget scope={`sector:${sectorId}`} />
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="bg-white border border-fp-border p-5 sm:p-6 lg:p-8">
                        <div className="mb-6">
                            <div className="font-sans text-[11px] uppercase tracking-[0.2em] text-fp-muted mb-1">Profitability</div>
                            <h3 className="font-serif text-2xl text-fp-ink">EBITDA & Margin</h3>
                        </div>
                        <EbitdaMarginChart periods={data.periods} ebitda={c.pnl.ebitda} marginPct={c.pnl.ebitda_margin_pct} />
                    </div>
                    <div className="bg-white border border-fp-border p-5 sm:p-6 lg:p-8">
                        <div className="mb-6">
                            <div className="font-sans text-[11px] uppercase tracking-[0.2em] text-fp-muted mb-1">Cash</div>
                            <h3 className="font-serif text-2xl text-fp-ink">Cash Flow Waterfall</h3>
                        </div>
                        <CashFlowChart periods={data.periods} ocf={c.cash_flow.operating_cash_flow} capex={c.cash_flow.capex} fcf={c.cash_flow.free_cash_flow} />
                    </div>
                </div>

                <div className="bg-white border border-fp-border p-5 sm:p-6 lg:p-8" data-testid="entity-breakdown">
                    <div className="mb-6">
                        <div className="font-sans text-[11px] uppercase tracking-[0.2em] text-fp-muted mb-1">Portfolio</div>
                        <h3 className="font-serif text-2xl text-fp-ink">Entity Contribution</h3>
                    </div>
                    <table className="w-full">
                        <thead>
                            <tr className="border-b-2 border-fp-ink">
                                <th className="text-left pb-2 font-sans text-[10px] uppercase tracking-wider text-fp-secondary">Entity</th>
                                <th className="text-left pb-2 font-sans text-[10px] uppercase tracking-wider text-fp-secondary">Country</th>
                                <th className="text-right pb-2 font-sans text-[10px] uppercase tracking-wider text-fp-secondary">Revenue</th>
                                <th className="text-right pb-2 font-sans text-[10px] uppercase tracking-wider text-fp-secondary">EBITDA</th>
                                <th className="text-right pb-2 font-sans text-[10px] uppercase tracking-wider text-fp-secondary">Margin</th>
                                <th className="text-right pb-2 font-sans text-[10px] uppercase tracking-wider text-fp-secondary">ROIC</th>
                                <th className="pb-2" />
                            </tr>
                        </thead>
                        <tbody>
                            {data.entity_breakdown.map((e) => (
                                <tr key={e.entity_id} className="border-b border-fp-border hover:bg-fp-bg transition-colors">
                                    <td className="py-3 font-sans text-sm text-fp-ink">{e.entity_name}</td>
                                    <td className="py-3 font-mono text-xs text-fp-secondary">{e.country_code}</td>
                                    <td className="py-3 text-right font-mono text-sm text-fp-ink">{fmtMoney(e.revenue_musd)}</td>
                                    <td className="py-3 text-right font-mono text-sm text-fp-ink">{fmtMoney(e.ebitda_musd)}</td>
                                    <td className="py-3 text-right font-mono text-sm text-fp-secondary">{fmtPctPlain(e.ebitda_margin_pct)}</td>
                                    <td className="py-3 text-right font-mono text-sm text-fp-secondary">{fmtPctPlain(e.roic_pct)}</td>
                                    <td className="py-3 text-right">
                                        <Link to={`/entity/${e.entity_id}/portal`} data-testid={`drill-entity-${e.entity_id}`} className="text-fp-secondary hover:text-fp-ink"><ArrowRight size={14} /></Link>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
